import { date } from 'quasar'
import { imgixHost, isAdmin, region } from 'src/config'
import { EN } from 'src/i18n/index'
import Role from 'src/models/access-control/Role'
import Card from 'src/models/card/Card'
import CashCount from 'src/models/cash/CashCount'
import CashSession from 'src/models/cash/CashSession'
import Circuit from 'src/models/circuit/Circuit'
import GiftCard from 'src/models/gift-card/GiftCard'
import Membership from 'src/models/membership/Membership'
import { MEMBERSHIP_DISCOUNT_CONDITIONS, MEMBERSHIP_DISCOUNT_RENEWAL_TERMS } from 'src/models/membership-type/MembershipDiscount'
import MembershipTier from 'src/models/membership-type/MembershipTier'
import MembershipType from 'src/models/membership-type/MembershipType'
import { MEMBERSHIP_VOUCHER_CONDITIONS, MEMBERSHIP_VOUCHER_RENEWAL_TERMS } from 'src/models/membership-type/MembershipVoucher'
import Model, { encoders, executeMutation, GqlError, ID, Integer, IsoDate, IsoDateTime, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/Model'
import Report from 'src/models/report/Report'
import Site, { SHOWTIME_LIST_VIEW_POS_OPTIONS } from 'src/models/site/Site'
import UserMarketingSubscription from 'src/models/subscription/UserMarketingSubscription'
import TimeClockSession from 'src/models/time-clock/TimeClockSession'
import UserEmployeeJobType from 'src/models/user/UserEmployeeJobType'
import UserClass from 'src/models/user-class/UserClass'
import UserMovie from 'src/models/user-movie/UserMovie'
import Voucher from 'src/models/voucher/Voucher'
const { addToDate, buildDate, isBetweenDates } = date

export const USER_TYPES = {
  STANDARD: 'standard',
  MARKETING: 'marketing',
  DAYPASS: 'day-pass',
  GUEST: 'member-guest'
}
USER_TYPES.ALL = _.values(USER_TYPES)

export default class User extends Model {
  static name = 'User'

  static versioned = true
  static paginate = true
  static deletable = isAdmin
  static isExportable = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Customer, Model.permissionCategories.Team]
  get isManagePermitted () {
    if (this.isDeleted) {
      return super.isManagePermitted && this.context.currentUser?.superUser
    }

    return super.isManagePermitted
  }

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      type: {
        type: String,
        output: true,
        default: null
      },
      firstName: {
        type: String,
        default: '',
        sortable: true
      },
      lastName: {
        type: String,
        default: '',
        sortable: true
        // computed () {
        //   return this.configuration.currentCircuit?.demoMode && this.data.lastName ? maskedValue(this.data.lastName) : this.data.lastName
        // }
      },
      email: {
        type: String,
        default: null,
        sortable: true
        // computed () {
        //   return this.configuration.currentCircuit?.demoMode && this.data.email ? maskedValue(this.data.email, 2, 4) : this.data.email
        // }
      },
      password: {
        type: String,
        default: null,
        local: true
      },
      accountClaimed: {
        type: Boolean,
        output: true
      },
      uid: {
        type: String,
        output: true
      },
      phone: {
        type: String,
        default: null,
        sortable: true
      },
      isInvoiceable: {
        type: Boolean,
        default: false
      },
      companyName: {
        type: String,
        default: null
      },
      billingAddressDetails: {
        type: JsonArray,
        default () {
          return []
        }
      },
      userMarketingSubscriptions: {
        type: [UserMarketingSubscription],
        acceptsNestedAttributes: true,
        allowDestroy: true
      },
      acknowledgedTermsOfService: {
        type: Boolean,
        default: false
      },
      acknowledgedAdminTermsOfService: {
        type: Boolean,
        default: false
      },
      receiveMarketingEmails: {
        type: Boolean,
        default: false,
        input: true
      },
      receiveMarketingTexts: {
        type: Boolean,
        default: null,
        input: true
      },
      emailShowtimes: {
        type: Boolean,
        default: true
      },
      textShowtimes: {
        type: Boolean,
        default: true
      },
      emailLastChance: {
        type: Boolean,
        default: true
      },
      emailPromotions: {
        type: Boolean,
        default: true
      },
      emailNewsletter: {
        type: Boolean,
        default: true
      },
      customerioUserProfileUrl: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      textLastChance: {
        type: Boolean,
        default: true
      },
      hasEffectiveMembership: {
        type: Boolean,
        default: false,
        excludeFromDefault: true,
        output: true
      },
      membership: {
        type: Membership,
        reference: true,
        encode: encoders.hasOne
      },
      membershipType: {
        type: MembershipType,
        reference: true,
        encode: encoders.hasOne
      },
      membershipTypeUpdatedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      membershipTier: {
        type: MembershipTier,
        default: null,
        reference: true,
        encode: encoders.hasOne,
        output: true
      },
      membershipTierRequirements: {
        type: JsonObject,
        default: () => ({}),
        output: true
      },
      membershipTierDowngradesAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      membershipTierUpdatedAt: {
        type: IsoDateTime,
        default: null,
        output: true
      },
      purchasers: {
        type: [User],
        reference: true,
        output: true
      },
      purchasees: {
        type: [User],
        reference: true,
        output: true
      },
      dateOfBirth: {
        type: String,
        default: null
      },
      dateOfBirthVerified: {
        type: Boolean,
        default: false
      },
      userClass: {
        type: UserClass,
        reference: true,
        encode: encoders.hasOne
      },
      fullAddress: {
        type: JsonObject,
        default () {
          return {
            formatted_address: null,
            address_components: [],
            address_line_1: null,
            address_line_2: null,
            city: null,
            state: null,
            post_code: null
          }
        }
      },
      card: {
        type: Card,
        allowDestroy: true
      },
      selectedAt: {
        type: IsoDateTime,
        sortable: true,
        default: null
      },
      employee: {
        type: Boolean,
        default: false,
        output: true
      },
      admin: {
        type: Boolean,
        default: false,
        output: true
      },
      pin: {
        type: String,
        input: true
      },
      employeeId: {
        type: String,
        default: null
      },
      userEmployeeJobTypes: {
        type: [UserEmployeeJobType],
        allowDestroy: true
      },
      photoId: {
        type: String,
        output: true
      },
      hasPhoto: {
        type: Boolean,
        output: true,
        default: false
      },
      photo: {
        type: String,
        default: '5xddmfetgz9dslyg19rhq1uwkp9r'
      },
      photoParams: {
        type: String,
        default: 'fit=facearea&facepad=3&w=500&height=500&auto=format',
        local: true
      },
      photoMediumParams: {
        type: String,
        default: 'fit=facearea&facepad=1.8&w=500&height=500&auto=format',
        local: true
      },
      photoSmallParams: {
        type: String,
        default: 'fit=facearea&facepad=0&w=50&height=50&auto=format',
        local: true
      },

      seatPreferences: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      itemPreferences: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      dashboard: {
        type: Report,
        reference: true,
        encode: encoders.hasOne
      },

      userMovies: {
        type: [UserMovie],
        output: true
      },
      watchedMovieIds: {
        type: [ID],
        output: true
      },

      countOfMovies: {
        type: Integer,
        output: true,
        excludeFromDefault: true
      },
      recentMovies: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      },
      topMovies: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      topGenres: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },

      superHero: {
        type: Boolean,
        output: true
      },
      superUser: {
        type: Boolean,
        output: true
      },
      adminMenuFavorites: {
        type: JsonArray,
        default () {
          return []
        }
      },
      posShowtimeViewPreference: {
        type: String,
        options: [
          { label: 'By Film', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM },
          { label: 'By Film Compact', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_FILM_COMPACT },
          { label: 'By Cinema', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_CINEMA },
          { label: 'By Schedule', value: SHOWTIME_LIST_VIEW_POS_OPTIONS.SHOWTIMES_BY_SCHEDULE }
        ]
      },
      overridePosShowtimeViewPreference: {
        type: Boolean,
        default: false
      },
      compactTablePreference: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      plainTableContentPreference: {
        type: Boolean,
        default: false,
        excludeFromDefault: true
      },
      seenClientVersion: {
        type: String
      },
      circuit: {
        type: Circuit,
        reference: true,
        encode: encoders.hasOne
      },
      site: {
        type: Site,
        reference: true,
        encode: encoders.hasOne
      },
      authorizedCircuits: {
        type: [Circuit],
        output: true
      },
      authorizedSites: {
        type: [Site],
        default: () => { return [] },
        encode (data) {
          data.authorizedSiteIds = _.map(this.authorizedSites.data, 'id')
        },
        output: true
      },
      authorizedSiteIds: {
        type: [ID],
        output: true
      },
      credit: {
        type: Number,
        default: 0,
        output: true
      },
      newCreditBalance: {
        type: Number,
        default: 0,
        input: true,
        encode: encoders.cents
      },
      points: {
        type: Integer,
        output: true,
        default: 0
      },
      discountRedemptions: {
        type: JsonObject,
        output: true,
        default: () => ({}),
        excludeFromDefault: true,
        required: false
      },
      redemptionsByShowing: {
        type: JsonObject,
        output: true,
        default: () => ({ discounts: {}, voucher_types: {} }),
        excludeFromDefault: true,
        required: false
      },
      redemptionsByActivitySession: {
        type: JsonObject,
        output: true,
        default: () => ({ discounts: {}, voucher_types: {} }),
        excludeFromDefault: true,
        required: false
      },
      newPointsBalance: {
        type: Integer,
        input: true,
        default: null
      },
      newPointsExpiresAt: {
        type: IsoDate,
        input: true,
        default: null
      },
      newVisit: {
        type: IsoDate,
        input: true,
        default: null
      },
      excludeFromUserList: {
        type: Boolean,
        default: false
      },
      vouchers: {
        type: [Voucher],
        reference: true,
        output: true
      },
      giftCards: {
        type: [GiftCard],
        reference: true,
        output: true
      },
      defaultPaymentMethodId: {
        type: String,
        output: true
      },
      defaultPaymentMethodFingerprint: {
        type: String,
        output: true
      },
      defaultPaymentMethodBrand: {
        type: String,
        output: true
      },
      defaultPaymentMethodLast4: {
        type: String,
        output: true
      },
      attachPaymentMethodId: {
        type: String,
        input: true
      },
      stripeSetupIntentClientSecret: {
        type: String,
        output: true,
        excludeFromDefault: true
      },

      hasPendingCharge: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      failureCode: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      failureMessage: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      pendingAmount: {
        type: String,
        output: true,
        excludeFromDefault: true
      },

      dayPassUser: {
        type: Boolean,
        input: true,
        default: false
      },
      dayPassDate: {
        type: IsoDate,
        output: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },
      isDeleted: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },
      circuitAuthorized: {
        type: Boolean,
        default: false,
        output: true
      },
      hasPin: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      },
      timeClockSessions: {
        type: [TimeClockSession],
        output: true
      },
      action: {
        type: String,
        default: null,
        input: true
      },
      shouldPromptForMarketingEmails: {
        type: Boolean,
        excludeFromDefault: true,
        output: true
      },
      preferredLanguage: {
        type: String,
        default: EN
      },
      roles: {
        type: [Role],
        reference: true,
        encode (data) {
          data.roleIds = _.map(this.roles.data, 'id')
        },
        default: () => ([])
      },
      permittedFrontendActions: {
        type: [String],
        default: () => ([]),
        excludeFromDefault: true,
        output: true
      },
      intercomAuthenticationToken: {
        type: String,
        output: true,
        excludeFromDefault: true
      },
      intercomIndyUserId: {
        type: String,
        output: true,
        excludeFromDefault: true
      },

      activeCashCount: {
        type: CashCount,
        output: true,
        default: null
      },
      activeCashSession: {
        type: CashSession,
        output: true,
        default: null
      },
      lastCashSession: {
        type: CashSession,
        output: true,
        default: null
      }
    }

    this.computed = {
      photoUrl () {
        if (!this.photo) return

        return `${imgixHost}/${this.photo}?${this.photoParams}${this.configuration.currentCircuit.demoMode ? '&px=35' : ''}`
      },
      photoMediumUrl () {
        if (!this.photo) return

        return `${imgixHost}/${this.photo}?${this.photoMediumParams}${this.configuration.currentCircuit.demoMode ? '&px=35' : ''}`
      },
      photoSmallUrl () {
        if (!this.photo) return

        return `${imgixHost}/${this.photo}?${this.photoSmallParams}${this.configuration.currentCircuit.demoMode ? '&px=5' : ''}`
      },
      missingMembershipFields () {
        return !this.membershipType || this.membershipType.isLocal || !this.userClass || this.userClass.isLocal
      },
      canBeMembershipOwner () {
        const userHasInfo = !this.missingMembershipFields && !!this.email
        return userHasInfo && (
          this.defaultPaymentMethodId || this.membership?.canHaveOwnerWithoutPaymentInfo
        )
      },
      isMembershipOwner () {
        return this.membership?.owner?.id === this.id
      },
      isInitialMember () {
        if (!this.membership) return true
        return this.membership?.initialMember?.id === this.id
      },
      tierDaysRemaining () {
        if (!this.membershipTierDowngradesAt) return null

        return Math.round(((new Date(this.membershipTierDowngradesAt)).getTime() - (new Date()).getTime()) / (1000 * 60 * 60 * 24))
      },
      unlimitedMembershipVouchers () {
        if (!this.effectiveMembershipType) return []

        return _.filter(this.effectiveMembershipType.activeMembershipVouchers, membershipVoucher => {
          if (membershipVoucher.limitRedemptions) return false

          if (membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER]) {
            if (!_.includes(membershipVoucher.membershipTierIds, this.membershipTier?.id)) return false
          }

          if (membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.LIFETIME]) {
            const lifetime = (new Date()).getTime()

            let lifetimeMinimum = null
            let lifetimeMaximum = null
            if (membershipVoucher.conditionsRequirements.lifetimeTerm === MEMBERSHIP_VOUCHER_RENEWAL_TERMS.YEARS) {
              lifetimeMinimum = addToDate(new Date(this.createdAt), { year: membershipVoucher.conditionsRequirements.lifetimeMinimum }).getTime()
              if (membershipVoucher.conditionsRequirements.lifetimeMaximum !== null) lifetimeMaximum = addToDate(new Date(this.createdAt), { year: membershipVoucher.conditionsRequirements.lifetimeMaximum }).getTime()
            } else {
              lifetimeMinimum = addToDate(new Date(this.createdAt), { month: membershipVoucher.conditionsRequirements.lifetimeMinimum }).getTime()
              if (membershipVoucher.conditionsRequirements.lifetimeMaximum !== null) lifetimeMaximum = addToDate(new Date(this.createdAt), { month: membershipVoucher.conditionsRequirements.lifetimeMaximum }).getTime()
            }

            if (lifetime < lifetimeMinimum) return false

            if (lifetimeMaximum !== null && lifetime > lifetimeMaximum) return false
          }

          if (membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.LONGEVITY]) {
            const longevity = (new Date()).getTime()

            let longevityMinimum = null
            let longevityMaximum = null
            if (membershipVoucher.conditionsRequirements.longevityTerm === MEMBERSHIP_VOUCHER_RENEWAL_TERMS.YEARS) {
              longevityMinimum = addToDate(new Date(this.membershipTypeUpdatedAt), { year: membershipVoucher.conditionsRequirements.longevityMinimum }).getTime()
              if (membershipVoucher.conditionsRequirements.longevityMaximum !== null) longevityMaximum = addToDate(new Date(this.membershipTypeUpdatedAt), { year: membershipVoucher.conditionsRequirements.longevityMaximum }).getTime()
            } else {
              longevityMinimum = addToDate(new Date(this.membershipTypeUpdatedAt), { month: membershipVoucher.conditionsRequirements.longevityMinimum }).getTime()
              if (membershipVoucher.conditionsRequirements.longevityMaximum !== null) longevityMaximum = addToDate(new Date(this.membershipTypeUpdatedAt), { month: membershipVoucher.conditionsRequirements.longevityMaximum }).getTime()
            }

            if (longevity < longevityMinimum) return false

            if (longevityMaximum !== null && longevity > longevityMaximum) return false
          }

          if (membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.BIRTHDAY]) {
            const now = new Date()
            if (!this.dateMatchesBirthDate(now)) return false
          }

          return true
        })
      },
      unlimitedMembershipVoucherTypes () {
        return _.map(this.unlimitedMembershipVouchers, membershipVoucher => { return membershipVoucher.voucherType })
      },
      unlimitedMembershipTicketVoucherTypes () {
        return _.filter(this.unlimitedMembershipVoucherTypes, { type: 'ticket' })
      },
      membershipDiscounts () {
        return _.filter(this.effectiveMembershipType.activeMembershipDiscounts, membershipDiscount => {
          if (membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER]) {
            if (!_.includes(membershipDiscount.membershipTierIds, this.membershipTier?.id)) return false
          }

          if (membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY]) {
            const longevity = (new Date()).getTime()

            let longevityMinimum = null
            let longevityMaximum = null
            if (membershipDiscount.conditionsRequirements.loyaltyTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS) {
              longevityMinimum = addToDate(new Date(this.membershipTypeUpdatedAt), { year: membershipDiscount.conditionsRequirements.longevityMinimum }).getTime()
              if (membershipDiscount.conditionsRequirements.longevityMaximum !== null) longevityMaximum = addToDate(new Date(this.membershipTypeUpdatedAt), { year: membershipDiscount.conditionsRequirements.longevityMaximum }).getTime()
            } else {
              longevityMinimum = addToDate(new Date(this.membershipTypeUpdatedAt), { month: membershipDiscount.conditionsRequirements.longevityMinimum }).getTime()
              if (membershipDiscount.conditionsRequirements.longevityMaximum !== null) longevityMaximum = addToDate(new Date(this.membershipTypeUpdatedAt), { month: membershipDiscount.conditionsRequirements.longevityMaximum }).getTime()
            }

            if (longevity < longevityMinimum) return false

            if (longevityMaximum !== null & longevity > longevityMaximum) return false
          }
          if (membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.BIRTHDAY]) {
            const now = new Date()
            if (!this.dateMatchesBirthDate(now)) return false
          }

          if (membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.BIRTHWEEK]) {
            const now = new Date()
            if (!this.dateMatchesBirthWeek(now)) return false
          }

          return true
        })
      },
      membershipBaseMonthlyRate () {
        if (!this.membershipType) return 0

        if (!this.membershipUserType) return 0

        if (this.membershipType.free) return 0

        if (this.isInitialMember) {
          return this.membershipUserType.monthlyRate
        } else {
          return this.membershipUserType.additionalMonthlyRate
        }
      },
      membershipMonthlyRate () {
        let rate = 0

        if (!this.membershipType) return rate

        if (!this.membershipUserType) return rate

        if (this.membershipType.free) return rate

        if (this.isInitialMember) {
          rate = this.membershipUserType.monthlyRate
        } else {
          rate = this.membershipUserType.additionalMonthlyRate
        }

        if (this.membershipType.getDiscountPercent(this.membership.term)) {
          rate = rate * (100 - (this.membershipType.getDiscountPercent(this.membership.term) || 0)) / 100
        }

        return rate || 0
      },
      name () {
        return this.displayFirstName + ' ' + this.displayLastName
      },
      displayFirstName () {
        if (this.firstName === '' && this.type === USER_TYPES.GUEST) {
          if (this.configuration.currentCircuit.allowGuests) {
            return 'Guest'
          } else {
            return this.userClass ? this.userClass.name : 'Anonymous'
          }
        } else {
          return this.firstName ? this.firstName : 'Anonymous'
        }
      },
      displayLastName () {
        if (this.lastName === '' && this.type === USER_TYPES.GUEST) {
          if (this.configuration.currentCircuit.allowGuests) {
            return this.userClass ? `(${this.userClass.name})` : 'Member'
          } else {
            return 'Member'
          }
        } else {
          return this.lastName ?? 'Member'
        }
      },
      isExistingCircuitUser () {
        if (!this.employee) return false
        return (_.every(_.map(this.configuration.currentCircuit.sites.data, 'id'), (id) => this.originalData.authorizedSiteIds.includes(id)))
      },
      isCircuitUser () {
        if (!this.employee) return false
        return (_.every(_.map(this.configuration.currentCircuit.sites.data, 'id'), (id) => this.authorizedSiteIds.includes(id)))
      },
      description () {
        return this.name
      },
      effectiveMembershipType () {
        // The hasEffectiveMembership field may or may not have been fetched (it is excluded by default)
        const membershipIsEffective = this.hasEffectiveMembership || this.membership?.effective
        return membershipIsEffective ? this.membershipType : this.configuration.defaultMembershipType
      },
      membershipUserType () {
        if (!this.membershipType) return null

        return this.membershipType.membershipUserTypeForUser(this)
      },
      currentSiteItemPreferences () {
        const itemIds = _.map(this.configuration.items.data, 'id')
        return _.filter(this.itemPreferences, itemPreference => _.includes(itemIds, itemPreference.item_id))
      },
      usualOrder () {
        const filteredItems = _.filter(this.currentSiteItemPreferences, itemPreference => itemPreference.frequency > 0.15)

        return filteredItems.length ? filteredItems : null
      },
      isDayPassUser () {
        return this.type === USER_TYPES.DAYPASS
      },
      validGiftCards () {
        if (this.giftCards) {
          return _.filter(this.giftCards.data, giftCard => giftCard.balance > 0 && giftCard.state === 'valid')
        }
        return []
      },
      giftCardBalance () {
        return _.sumBy(this.validGiftCards, 'balance')
      },
      validGiftCardsForCurrentCircuit () {
        return _.filter(this.validGiftCards, giftCard => giftCard.circuitId === this.configuration.currentCircuit.id)
      },
      currentCircuitGiftCardsBalance () {
        return _.sumBy(this.validGiftCardsForCurrentCircuit, 'balance')
      },
      userEmployeeJobTypesForSite () {
        return _.filter(this.userEmployeeJobTypes?.data, userEmployeeJobType => userEmployeeJobType.employeeJobType.siteId === this.configuration.currentSite.id)
      },
      defaultEmployeeJobType () {
        return _.find(this.userEmployeeJobTypesForSite, { default: true }) || _.first(this.userEmployeeJobTypesForSite)
      }
    }

    this.fragments = {
      search: [
        'id', 'photo', 'hasPhoto', 'photoParams', 'photoSmallParams', 'photoMediumParams', 'firstName',
        'lastName', 'email', 'phone', 'card', 'employee', 'membershipTypeId', 'hasEffectiveMembership'],
      marketing: ['id', 'firstName', 'lastName', 'email', 'phone', 'userMarketingSubscriptions'],
      anonymous: ['id', 'email', 'shouldPromptForMarketingEmails'],
      operator: ['id', 'firstName', 'lastName', 'employee', 'admin'],
      identify: ['id', 'firstName', 'lastName', 'email']
    }

    this.queries = {
      currentUser: {
        results: {
          user: User
        }
      },
      findUserByUid: {
        arguments: {
          uid: {
            type: String,
            required: true
          }
        },
        results: {
          user: User
        }
      },
      findUserByEmail: {
        arguments: {
          email: {
            type: String,
            required: true
          }
        },
        results: {
          user: User
        }
      },
      findAnonymousUserByEmail: {
        arguments: {
          email: {
            type: String,
            required: true
          }
        },
        results: {
          user: User
        }
      },
      findUserByName: {
        arguments: {
          firstName: {
            type: String,
            required: true
          },
          lastName: {
            type: String,
            required: true
          }
        },
        results: {
          user: User
        }
      },
      findUserByMarketingSubscriptionToken: {
        arguments: {
          token: {
            type: String,
            required: true
          }
        },
        results: {
          user: User
        }
      }
    }

    this.mutations = {
      logIn: {
        arguments: {
          email: {
            type: String,
            required: true
          },
          password: {
            type: String,
            required: true
          },
          kioskLogin: {
            type: Boolean,
            required: false
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      registerUser: {
        arguments: {
          firstName: {
            type: String,
            required: true
          },
          lastName: {
            type: String,
            required: true
          },
          email: {
            type: String,
            required: true
          },
          dateOfBirth: {
            type: String,
            required: false
          },
          userClassId: {
            type: ID,
            required: false
          },
          fullAddress: {
            type: JsonObject,
            required: false
          },
          password: {
            type: String,
            required: true
          },
          acknowledgedTermsOfService: {
            type: Boolean,
            required: false
          },
          receiveMarketingEmails: {
            type: Boolean,
            required: false
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      logOut: {
        results: {
          success: Boolean
        }
      },
      logOutOfAllSessions: {
        arguments: {
          userId: {
            type: ID,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      },
      logInByCard: {
        arguments: {
          userId: {
            type: ID,
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      authorizeByCard: {
        arguments: {
          userId: {
            type: ID,
            required: true
          }
        },
        results: {
          authorization: Boolean,
          errors: [GqlError]
        }
      },
      logInByPin: {
        arguments: {
          pin: {
            type: String,
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      authorizeByPin: {
        arguments: {
          pin: {
            type: String,
            required: true
          }
        },
        results: {
          authorization: Boolean,
          errors: [GqlError]
        }
      },
      resetPassword: {
        arguments: {
          token: {
            type: String,
            required: true
          },
          password: {
            type: String,
            required: true
          },
          acknowledgedTermsOfService: {
            type: Boolean,
            required: false
          },
          receiveMarketingEmails: {
            type: Boolean,
            required: false
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      requestPasswordReset: {
        arguments: {
          email: {
            type: String,
            required: true
          },
          password: {
            type: String,
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      useResetPasswordToken: {
        arguments: {
          token: {
            type: String,
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      updateUserMarketingSubscriptions: {
        arguments: {
          token: {
            type: String,
            required: true
          },
          userMarketingSubscriptions: {
            type: [UserMarketingSubscription],
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      createUserMarketingSubscriptions: {
        arguments: {
          email: {
            type: String,
            required: true
          },
          firstName: {
            type: String,
            required: false
          },
          lastName: {
            type: String,
            required: false
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      failedPaymentDetailsByToken: {
        arguments: {
          token: {
            type: String,
            required: true
          }
        },
        results: {
          user: User,
          hasPendingCharge: Boolean,
          pendingAmount: Number,
          failureCode: String,
          failureMessage: String,
          errors: [GqlError]
        }
      },
      updatePaymentMethod: {
        arguments: {
          token: {
            type: String,
            required: true
          },
          paymentMethodAttributes: {
            type: JsonObject,
            required: true
          }
        },
        results: {
          user: User,
          errors: [GqlError]
        }
      },
      retryPendingPayments: {
        arguments: {
          token: {
            type: String,
            required: true
          }
        },
        results: {
          user: User,
          hasPendingCharge: Boolean,
          pendingAmount: Number,
          failureCode: String,
          failureMessage: String,
          errors: [GqlError]
        }
      },
      updatePreferredLanguage: {
        arguments: {
          preferredLanguage: {
            type: String,
            required: true
          }
        },
        results: {
          preferredLanguage: String,
          errors: [GqlError]
        }
      },
      promoteAccessLevel: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          makeEmployee: {
            type: Boolean,
            required: false
          },
          makeAdmin: {
            type: Boolean,
            required: false
          },
          makeCircuitLevel: {
            type: Boolean,
            required: false
          },
          newAuthorizedSiteIds: {
            type: [ID],
            required: false
          },
          sendResetPasswordEmail: {
            type: Boolean,
            required: false,
            default: false
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      },
      demoteAccessLevel: {
        arguments: {
          id: {
            type: ID,
            required: true
          },
          makeEmployee: {
            type: Boolean,
            required: false
          },
          makeNonEmployee: {
            type: Boolean,
            required: false
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      },
      addGiftCard: {
        arguments: {
          giftCardUid: {
            type: String,
            required: true
          }
        },
        results: {
          success: Boolean,
          giftCard: GiftCard,
          errors: [GqlError]
        }
      }
    }
  }

  get stateIcon () {
    switch (this.membership?.state) {
      case 'active':
        return { name: 'check', color: 'positive', tooltip: 'Active' }
      case 'pending':
        return { name: 'help', color: 'warning', tooltip: 'Pending' }
      case 'inactive':
        return { name: 'cancel', color: 'negative', tooltip: 'Inactive' }
      default:
        return null
    }
  }

  get deletionWarningText () {
    if (isAdmin && this.id === this.membership?.owner?.id && Number(this.membership?.currentMembershipInterval?.amount) > 0) {
      return 'Deleting this user will cancel their active paid membership'
    }

    return null
  }

  // Only return a dashboard that matches the current circuit
  get customDashboard () {
    if (!this.dashboard) return null

    if (this.dashboard.circuitId !== this.circuitId) return null

    return this.dashboard
  }

  get circuitMigratedUrl () {
    const userCircuit = _.find(this.authorizedCircuits.data, (authorizedCircuit) => {
      return authorizedCircuit.id === this.circuitId
    })

    if (!userCircuit?.migratedTo) return null

    if (region === 'us' && !window.location.href.includes('-us.')) {
      return window.location.href.replace('.indy.systems', `-${userCircuit.migratedTo}.indy.systems`)
    } else {
      return window.location.href.replace(region, userCircuit.migratedTo)
    }
  }

  get authorizedCircuitOptions () {
    const orderedCircuits = _.sortBy(_.sortBy(this.authorizedCircuits.data, ['isLibrary', 'name']), o => { return !!o.migratedTo })
    return _.map(orderedCircuits, circuit => {
      const circuitName = circuit.migratedTo ? `${circuit.name} (Migrated to ${circuit.migratedTo})` : circuit.name

      return { id: circuit.id, name: circuitName }
    })
  }

  get isAuthorizedForLibrarySite () {
    return _.some(this.authorizedSites.data, site => site.id === 0)
  }

  get authorizedSiteOptions () {
    return _(this.authorizedSites.data)
      .filter(authorizedSite => authorizedSite.name !== 'LIBRARY' && authorizedSite?.circuit?.id === this.circuitId)
      .sortBy('name')
      .map(authorizedSite => _.pick(authorizedSite, ['id', 'name']))
      .value()
  }

  get libraryAndAuthorizedSiteOptions () {
    return _(this.authorizedSites.data)
      .filter(authorizedSite => authorizedSite?.circuit?.id === this.circuitId)
      .sortBy(['isLibrary', 'name'])
      .map(authorizedSite => _.pick(authorizedSite, ['id', 'name']))
      .value()
  }

  getUserMarketingSubscription (marketingSubscription) {
    return _.find(this.userMarketingSubscriptions.data, { marketingSubscriptionId: marketingSubscription.id })
  }

  getMembershipMonthlyRate (term = this.membership.term) {
    let rate = 0

    if (!this.membershipType) return rate

    if (!this.membershipUserType) return rate

    if (this.membershipType.free) return rate

    if (this.isInitialMember) {
      rate = this.membershipUserType.monthlyRate
    } else {
      rate = this.membershipUserType.additionalMonthlyRate
    }

    if (this.membershipType.getDiscountPercent(term)) {
      rate = rate * (100 - (this.membershipType.getDiscountPercent(term) || 0)) / 100
    }

    return rate || 0
  }

  get itemPreferencesAsCategory () {
    const matchingPreferences = this.currentSiteItemPreferences

    if (!matchingPreferences || matchingPreferences.length === 0) return null

    const items = _(this.currentSiteItemPreferences).map(itemPreference => {
      const item = _.find(this.configuration.items.data, { id: itemPreference.item_id })

      if (!item) return

      const newItem = item.clone()
      newItem.itemPreference = itemPreference
      return newItem
    }).compact().unionBy('id').value()

    const firstSiteCategory = this.configuration.currentSite.availableOnlineCategories[0]

    const requireAllShowingBadges = firstSiteCategory?.requireAllShowingBadges || false
    const requireShowingBadges = firstSiteCategory?.requireShowingBadges || false
    const showingBadgeIds = firstSiteCategory?.showingBadgeIds || []

    return {
      name: `${this.firstName}'s Favorites`,
      imageUrl: 'https://indy-systems.imgix.net/knl9yxqgu44xfp4ngxh7okx3qr7f?w=190&h=118&fit=fill&fill=solid&fill-color=555&bg=555',
      availableOnline: true,
      items: { data: items },
      itemIds: _.map(items, 'id'),
      requireAllShowingBadges,
      requireShowingBadges,
      showingBadgeIds
    }
  }

  async createSetupIntent (terminal = false, errorHandler) {
    // Default error handler
    if (!errorHandler) {
      errorHandler = errors => {
        console.log(errors)
      }
    }

    let errors = this.errors

    if (errors) {
      errorHandler(errors)
      return
    }

    // Set the action we want the server to execute
    let action
    if (terminal) {
      action = 'create_terminal_setup_intent'
    } else {
      action = 'create_setup_intent'
    }

    const intentUser = new User({
      id: this.id,
      action
    }, {
      fields: ['id', 'stripeSetupIntentClientSecret']
    })

    try {
      const result = await intentUser.save()
      if (result.errors.length) {
        errors = result.errors
      }
    } catch (error) {
      errors = [error]
    }

    if (this.isLocal && intentUser.isRemote) {
      this.id = intentUser.id
    }

    if (errors) {
      errorHandler(errors)
      return
    }

    return intentUser.stripeSetupIntentClientSecret
  }

  async updateDefaultPaymentMethodFromStripe () {
    this.action = 'update_default_payment_method_from_stripe'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async removeDefaultPaymentMethod () {
    this.action = 'remove_default_payment_method'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async attachPaymentMethod (paymentMethodId) {
    this.attachPaymentMethodId = paymentMethodId

    this.action = 'attach_payment_method'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async makeStandardUser () {
    this.action = 'make_standard_user'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async editPoints () {
    this.action = 'points_adjust'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async adjustCredit () {
    this.action = 'adjust_credit'
    this.newCreditBalance = this.credit

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async addVisit () {
    this.action = 'add_visit'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async logIn (kioskLogin = false) {
    const results = await executeMutation(
      'logIn',
      { email: this.email, password: this.password, kioskLogin },
      {
        user: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async logOut () {
    await executeMutation('logOut', {}, { success: true })
  }

  async logOutOfAllSessions (userId) {
    const results = await executeMutation(
      'logOutOfAllSessions',
      { userId },
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  /**
   * Generates a color that can be used to visually distinguish users.
   *
   * @returns {string} - Returns a hexadecimal color code.
   */
  getColorForUser () {
    const reversedId = this.id.toString().split('').reverse().join('')

    const stringToConvert = reversedId + this.firstName + this.lastName

    const toHex = (str) => {
      let hash = 0
      str.split('').forEach(char => {
        hash = char.charCodeAt(0) + ((hash << 5) - hash)
      })
      let colour = ''
      for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff
        colour += value.toString(16).padStart(2, '0')
      }
      return colour
    }
    return toHex(stringToConvert)
  }

  /**
   * Creates a new user account.
   *
   * @async
   * @returns {Object} - Returns the results of the registration, including user details and any errors.
   */
  async register () {
    const results = await executeMutation(
      'registerUser',
      {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        dateOfBirth: this.dateOfBirth,
        userClassId: this.userClassId,
        fullAddress: this.fullAddress,
        password: this.password,
        acknowledgedTermsOfService: this.acknowledgedTermsOfService,
        receiveMarketingEmails: this.receiveMarketingEmails
      },
      {
        user: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async createUserMarketingSubscriptions () {
    const results = await executeMutation(
      'createUserMarketingSubscriptions',
      {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        receiveMarketingEmails: this.receiveMarketingEmails
      },
      {
        user: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async resetPassword (token, password) {
    const results = await executeMutation(
      'resetPassword',
      {
        token,
        password,
        acknowledgedTermsOfService: this.acknowledgedTermsOfService,
        receiveMarketingEmails: this.receiveMarketingEmails
      },
      {
        user: {
          structure: {
            id: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async requestPasswordReset () {
    const results = await executeMutation(
      'requestPasswordReset',
      {
        email: this.email
      },
      {
        user: {
          structure: {
            id: true,
            email: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async useResetPasswordToken (token) {
    const results = await executeMutation(
      'useResetPasswordToken',
      {
        token
      },
      {
        user: {
          structure: {
            id: true,
            email: true,
            accountClaimed: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async updatePreferredLanguage (preferredLanguage) {
    const results = await executeMutation(
      'updatePreferredLanguage',
      {
        preferredLanguage
      },
      {
        preferredLanguage: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async promoteAccessLevel ({ makeEmployee = false, makeAdmin = false, makeCircuitLevel = false, newAuthorizedSiteIds = [], sendResetPasswordEmail = false } = {}) {
    const params = { id: this.id, makeEmployee, makeAdmin, makeCircuitLevel, newAuthorizedSiteIds, sendResetPasswordEmail }
    const results = await executeMutation(
      'promoteAccessLevel',
      params,
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async demoteAccessLevel ({ makeEmployee = false, makeNonEmployee = false } = {}) {
    const params = { id: this.id, makeEmployee, makeNonEmployee }
    const results = await executeMutation(
      'demoteAccessLevel',
      params,
      {
        success: true,
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async addGiftCard (giftCardCode) {
    if (_.isEmpty(giftCardCode)) return

    const params = {
      giftCardUid: giftCardCode
    }

    const results = await executeMutation(
      'addGiftCard',
      params,
      {
        success: true,
        giftCard: {
          structure: {
            id: true,
            uid: true,
            state: true,
            balance: true,
            expiresAt: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )
    return results
  }

  async updateUserMarketingSubscriptions (token, userMarketingSubscriptions) {
    const results = await executeMutation(
      'updateUserMarketingSubscriptions',
      {
        token,
        userMarketingSubscriptions
      },
      {
        user: {
          structure: {
            id: true,
            userMarketingSubscriptions: {
              id: true,
              allowEmail: true,
              allowPush: true,
              allowSms: true,
              marketingSubscriptionId: true
            }
          }
        }
      },
      {
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async retryFailedPayments () {
    this.action = 'retry_failed_payments'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  get canHaveRoles () {
    return this.employee || this.admin
  }

  dateOfBirthAsDate () {
    if (!this.dateOfBirth) return null

    // dateOfBirth is already an ISO date string. Using Quasar's date util we can parse the date string ensuring
    // the date day value is not changed due to time zone offset
    const [dobYear, dobMonth, dobDateDay] = _.split(this.dateOfBirth, '-', 3)
    return buildDate({ year: parseInt(dobYear), month: parseInt(dobMonth), date: parseInt(dobDateDay) }, false)
  }

  /**
   * Compares month and day numbers only, ignoring time zone.
   */
  dateMatchesBirthDate (dateObj) {
    if (!dateObj) return false
    if (!this.dateOfBirth) return false

    if (_.isString(dateObj)) {
      dateObj = new Date(dateObj)
    }

    const birthdayDate = this.dateOfBirthAsDate()

    return birthdayDate.getMonth() === dateObj.getMonth() && birthdayDate.getDate() === dateObj.getDate()
  }

  /**
   * Assumes the dateObj's time zone matches the device's local timezone.
   */
  dateMatchesBirthWeek (dateObj) {
    if (!dateObj) return false
    if (!this.dateOfBirth) return false

    if (_.isString(dateObj)) {
      dateObj = new Date(dateObj)
    }

    const birthdayDate = this.dateOfBirthAsDate()
    birthdayDate.setFullYear(dateObj.getFullYear())
    // Weekdays are numbered from 0 (Sunday) to 6 (Saturday)
    const lastWeekday = 6
    const startOfWeek = addToDate(new Date(birthdayDate), { days: -birthdayDate.getDay() })
    const endOfWeek = addToDate(new Date(birthdayDate), { days: lastWeekday - birthdayDate.getDay() })

    return isBetweenDates(dateObj, startOfWeek, endOfWeek, { inclusiveFrom: true, inclusiveTo: true, onlyDate: true })
  }

  toggleRole (role) {
    if (_.find(this.roles.data, { id: role.id })) {
      this.roles.data = _.filter(this.roles.data, ({ id }) => id !== role.id)
    } else {
      this.roles.data = [...this.roles.data, role]
    }
  }

  hasJobType (jobType) {
    return _.some(this.userEmployeeJobTypes?.data, (employeeJobType) => employeeJobType.employeeJobType.id === jobType.id)
  }

  hasSavedJobType (jobType) {
    const userEmployeeJobType = this.userEmployeeJobTypeByJobType(jobType)
    return userEmployeeJobType && !userEmployeeJobType.isLocal
  }

  userEmployeeJobTypeByJobType (jobType) {
    return _.find(this.userEmployeeJobTypes.data, (employeeJobType) => employeeJobType.employeeJobType.id === jobType.id)
  }

  addJobType (jobType) {
    if (this.hasJobType(jobType)) {
      return
    }

    const userEmployeeJobType = new UserEmployeeJobType({ employeeJobType: jobType })
    this.userEmployeeJobTypes.add(userEmployeeJobType)
  }

  removeJobType (jobType) {
    const jobTypeToRemove = _.find(this.userEmployeeJobTypes.data, (employeeJobType) => employeeJobType.employeeJobType.id === jobType.id)
    if (jobTypeToRemove) {
      this.userEmployeeJobTypes.remove(jobTypeToRemove)
    }
  }
}
